import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';

const TYPE_ICONS = {
  reminder:          '🔔',
  task_due:          '✅',
  contract_renewal:  '📋',
  rgpd_alert:        '🔒',
  info:              'ℹ️',
};

const TYPE_COLORS = {
  reminder:          '#41B073',
  task_due:          '#f59e0b',
  contract_renewal:  '#3b82f6',
  rgpd_alert:        '#ef4444',
  info:              '#22c55e',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  if (mins < 1)   return 'À l\'instant';
  if (mins < 60)  return `Il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days  = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [open, setOpen]   = useState(false);
  const dropdownRef       = useRef(null);
  const navigate          = useNavigate();

  // Fermer en cliquant en dehors
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  function handleNotifClick(notif) {
    if (!notif.read) markAsRead(notif.id);
    if (notif.link) navigate(notif.link);
    setOpen(false);
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Bouton cloche */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Notifications"
        style={{
          position:   'relative',
          background: 'transparent',
          border:     'none',
          cursor:     'pointer',
          fontSize:   20,
          padding:    '4px 6px',
          borderRadius: 6,
          color: open ? '#41B073' : '#94a3b8',
          transition: 'color 0.15s',
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span style={{
            position:   'absolute',
            top:        0,
            right:      0,
            background: '#ef4444',
            color:      '#fff',
            borderRadius: 10,
            padding:    '0 5px',
            fontSize:   10,
            fontWeight: 700,
            minWidth:   16,
            textAlign:  'center',
            lineHeight: '16px',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position:   'absolute',
          bottom:     '110%',
          left:       '50%',
          transform:  'translateX(-50%)',
          width:      340,
          background: '#1a1d2e',
          border:     '1px solid #2a2d3e',
          borderRadius: 10,
          boxShadow:  '0 8px 32px rgba(0,0,0,0.5)',
          zIndex:     1000,
          overflow:   'hidden',
        }}>
          {/* En-tête */}
          <div style={{
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            padding:        '12px 16px',
            borderBottom:   '1px solid #2a2d3e',
          }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#e2e8f0' }}>
              Notifications {unreadCount > 0 && <span style={{ color: '#ef4444' }}>({unreadCount})</span>}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{ background: 'none', border: 'none', color: '#41B073', cursor: 'pointer', fontSize: 12 }}
              >
                Tout marquer lu
              </button>
            )}
          </div>

          {/* Liste */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                Aucune notification
              </div>
            ) : (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  onClick={() => handleNotifClick(notif)}
                  style={{
                    display:    'flex',
                    alignItems: 'flex-start',
                    gap:        10,
                    padding:    '10px 16px',
                    cursor:     notif.link ? 'pointer' : 'default',
                    background: notif.read ? 'transparent' : '#41B07308',
                    borderLeft: notif.read ? '3px solid transparent' : `3px solid ${TYPE_COLORS[notif.type] || '#41B073'}`,
                    borderBottom: '1px solid #2a2d3e',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#ffffff08'}
                  onMouseLeave={e => e.currentTarget.style.background = notif.read ? 'transparent' : '#41B07308'}
                >
                  {/* Icône type */}
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                    {TYPE_ICONS[notif.type] || '🔔'}
                  </span>

                  {/* Contenu */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize:   13,
                      fontWeight: notif.read ? 400 : 600,
                      color:      notif.read ? '#94a3b8' : '#e2e8f0',
                      whiteSpace: 'nowrap',
                      overflow:   'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {notif.title}
                    </div>
                    {notif.message && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        {notif.message}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>
                      {timeAgo(notif.created_at)}
                    </div>
                  </div>

                  {/* Bouton supprimer */}
                  <button
                    onClick={e => { e.stopPropagation(); deleteNotification(notif.id); }}
                    style={{
                      background: 'none', border: 'none', color: '#475569',
                      cursor: 'pointer', fontSize: 14, padding: '0 2px', flexShrink: 0,
                    }}
                    title="Supprimer"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Pied */}
          {notifications.length > 0 && (
            <div style={{ padding: '8px 16px', borderTop: '1px solid #2a2d3e', textAlign: 'center' }}>
              <button
                onClick={() => { navigate('/agenda'); setOpen(false); }}
                style={{ background: 'none', border: 'none', color: '#41B073', cursor: 'pointer', fontSize: 12 }}
              >
                Voir l'agenda →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
